import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { preferencesSuccess } from "../User/redux/user.actions"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getPricingFilterOptionsRequest: null,
  getPricingFilterOptionsSuccess: ["payload"], // payload: { data }
  getPricingFilterOptionsFailure: ["payload"], // payload: { data }

  getPricingRequest: ["payload"], // payload: { params }
  getPricingSuccess: ["payload"], // payload: { data }

  createPricingRequest: ["payload"], // payload: { data }
  createPricingSuccess: ["payload"], // payload: { newPrice }

  updatePricingRequest: ["payload"], // payload: { data }
  updatePricingSuccess: ["payload"], // payload: { updatePrice }

  deletePricingRequest: ["payload"], // payload: { id }
  deletePricingSuccess: ["payload"], // payload: { data }

  pricingFailure: ["payload"], // payload: { errors }

  clearPricingFilters: null,
  pricingSetProp: ["key", "value"],

  getPricingExportRequest: ["payload"], // payload: { business_id }
  getPricingExportSuccess: ["payload"], // payload: { url }
  getPricingExportFailure: ["payload"], // payload: { errors }

  logout: null
})

export const PricingTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "pricing_schema", label: "Pricing scheme", active: true },
  { field_id: "type", label: "Type", active: true },

  { field_id: "ethoca_price", label: "Ethoca", active: true },
  { field_id: "rdr_1_price", label: "RDR", active: true },
  { field_id: "cdrn_price", label: "CDRN", active: true },

  { field_id: "count_of_free_alerts", label: "Number of the first alerts to skip", active: true },
  { field_id: "applied", label: "Applied", active: true }
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    pricing_schema: "all",
    type: "all",
    applied: "all",
    count_of_free_alerts: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    pricing_schema: [],
    type: [],
    applied: []
  },

  visibleFiltersAdmin: {
    pricing_schema: true,
    type: true,
    count_of_free_alerts: true,
    applied: true
  },

  exportFetching: false,
  exportResult: null,

  saveFetching: false,
  saveResult: null
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state) =>
  isEqual(state.pricing.filters, INITIAL_STATE.filters)

/* ------------- Functions for reducer cases ------------- */
const getPricingFilterOptionsRequest = (state) => state.merge({ fetchingFilterOptions: true })
const getPricingFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({ fetchingFilterOptions: false, filterOptions })
const getPricingFilterOptionsFailure = (state) => state.merge({ fetchingFilterOptions: false })

const getPricingRequest = (state) => state.merge({ fetching: true })
const getPricingSuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({
      fetching: false,
      result: { count: result?.results?.length || 0, ...result }
    })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const createPricingRequest = (state) => state.merge({ saveFetching: true })
const createPricingSuccess = (state, { payload: newPrice }) => {
  return state.merge({
    saveFetching: false,
    saveResult: "create"
    //result: {count: state.result?.count + 1, results: [newPrice, ...state.result.results]}
  })
}

const updatePricingRequest = (state) => state.merge({ saveFetching: true })
const updatePricingSuccess = (state, { payload: newPrice }) => {
  return state.merge({
    saveFetching: false,
    saveResult: "update",
    result: {
      ...state.result,
      results: state.result.results.map((price) =>
        price.id === newPrice.id ? { ...price, ...newPrice } : price
      )
    }
  })
}

const deletePricingRequest = (state) => state.merge({ fetching: true })
const deletePricingSuccess = (state, { payload: deletePrice }) => {
  return state.merge({
    fetching: false,
    result: {
      count: state.result?.count - 1,
      results: [...state.result.results.filter((price) => price.id !== deletePrice.id)]
    }
  })
}

const pricingFailure = (state) => state.merge({ fetching: false, saveFetching: false })

export const pricingSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

export const clearPricingFilters = (state) => {
  return state.merge({ filters: INITIAL_STATE.filters })
}

const getPricingExportRequest = (state) => state.merge({ exportFetching: true })
const getPricingExportSuccess = (state, { payload: exportResult }) =>
  state.merge({
    exportFetching: false,
    exportResult
  })
const getPricingExportFailure = (state) => state.merge({ exportFetching: false })

const logout = (state) => {
  return state.merge({
    ...INITIAL_STATE,
    filters: state.filters,
    columnsAdmin: state.columnsAdmin,
    visibleFiltersAdmin: state.visibleFiltersAdmin
  })
}

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const PRICING_PREFERENCES_VERSION = 1

const applyPreferences = (state, { payload }) => {
  const preference = payload?.pricing?.data
  if (!preference || preference.version !== PRICING_PREFERENCES_VERSION) return state
  if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
    return state

  return state.merge({
    filters: preference.filters,
    columnsAdmin: preference.columnsAdmin,
    visibleFiltersAdmin: preference.visibleFiltersAdmin
  })
}

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_PRICING_FILTER_OPTIONS_REQUEST]: getPricingFilterOptionsRequest,
  [Types.GET_PRICING_FILTER_OPTIONS_SUCCESS]: getPricingFilterOptionsSuccess,
  [Types.GET_PRICING_FILTER_OPTIONS_FAILURE]: getPricingFilterOptionsFailure,

  [Types.GET_PRICING_REQUEST]: getPricingRequest,
  [Types.GET_PRICING_SUCCESS]: getPricingSuccess,

  [Types.CREATE_PRICING_REQUEST]: createPricingRequest,
  [Types.CREATE_PRICING_SUCCESS]: createPricingSuccess,

  [Types.UPDATE_PRICING_REQUEST]: updatePricingRequest,
  [Types.UPDATE_PRICING_SUCCESS]: updatePricingSuccess,

  [Types.DELETE_PRICING_REQUEST]: deletePricingRequest,
  [Types.DELETE_PRICING_SUCCESS]: deletePricingSuccess,

  [Types.PRICING_FAILURE]: pricingFailure,

  [Types.CLEAR_PRICING_FILTERS]: clearPricingFilters,

  [Types.PRICING_SET_PROP]: pricingSetProp,

  [Types.GET_PRICING_EXPORT_REQUEST]: getPricingExportRequest,
  [Types.GET_PRICING_EXPORT_SUCCESS]: getPricingExportSuccess,
  [Types.GET_PRICING_EXPORT_FAILURE]: getPricingExportFailure,

  [Types.LOGOUT]: logout,

  [preferencesSuccess.type]: applyPreferences
})
